import { useEffect, useRef, useState } from 'react'
import type { FormEvent, KeyboardEvent } from 'react'
import { Sparkles } from 'lucide-react'
import { Button } from '~/components/ui/button'
import { Modal } from '~/components/ui/modal'
import { ChatThinking } from '~/components/chat/chat-thinking'
import { Typewriter } from '~/components/chat/typewriter'
import { defineAssessment } from '~/api/assessment'
import type { DefineMessage, DefineSlots } from '~/api/types'

export interface AiDefinePanelProps {
    /** AI가 추론한 슬롯이 갱신될 때마다 호출(폼 필드 자동 채움). */
    onSlots(slots: DefineSlots): void
    /** 패널 닫기. */
    onClose(): void
}

// 첫 질문은 아직 대화가 없어 항상 동일한 오프닝(규모 추론)이라 LLM을 돌릴 필요가 없다.
// 고정 오프닝을 즉시 띄우고, LLM은 사용자의 첫 답변부터 돌린다(체감 지연 제거).
// 문구·보기는 agent define 프롬프트의 슬롯1(scale) 전략과 동일하게 맞춘다.
const OPENING_MESSAGE =
    '안녕하세요! 어렵지 않은 질문 몇 개로 평가를 대신 정리해 드릴게요. 먼저, 매장(또는 공장)에서 함께 일하시는 분이 대략 몇 분 정도 되나요?'
const OPENING_CHOICES = ['직원 5명 이하', '10명 안팎', '20명 넘어요']

/**
 * 「AI로 정의」 챗 패널.
 * 간접적인 선택형 HITL 질문으로 대화하며 규모·업종·분류·단계·제목을 추론해
 * 매 턴 슬롯을 상위 폼으로 올려보내(onSlots) 폼 필드를 자동으로 채운다.
 * 대화는 누적되며, 사용자는 보기 칩 클릭 또는 자유 입력으로 답한다.
 */
export function AiDefinePanel({ onSlots, onClose }: AiDefinePanelProps) {
    const [messages, setMessages] = useState<DefineMessage[]>([])
    const [choices, setChoices] = useState<string[]>([])
    const [done, setDone] = useState(false)
    const [busy, setBusy] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [input, setInput] = useState('')
    /** 가장 마지막 AI 답변 index — 타이핑 효과는 여기에만 적용. */
    const [lastTypedIndex, setLastTypedIndex] = useState(-1)

    const scrollRef = useRef<HTMLDivElement>(null)
    const startedRef = useRef(false)

    const scrollToBottom = () => {
        const el = scrollRef.current
        if (el) el.scrollTop = el.scrollHeight
    }

    useEffect(() => {
        scrollToBottom()
    }, [messages, busy, choices])

    // 누적 대화에 한 턴을 더해 백엔드로 보내고, 응답을 반영한다.
    const advance = async (history: DefineMessage[]) => {
        setBusy(true)
        setError(null)
        setChoices([])
        try {
            const res = await defineAssessment(history)
            setMessages((prev) => {
                const next = [
                    ...prev,
                    {
                        role: 'assistant' as const,
                        content: res.assistantMessage,
                    },
                ]
                setLastTypedIndex(next.length - 1)
                return next
            })
            setChoices(res.choices ?? [])
            setDone(res.done)
            if (res.slots) onSlots(res.slots)
        } catch {
            setError('AI 응답을 가져오지 못했어요. 다시 시도해 주세요.')
        } finally {
            setBusy(false)
        }
    }

    // 패널이 열리면 고정 오프닝을 즉시 띄운다(첫 질문은 결정적이라 LLM 불필요).
    // 사용자가 답하면 그때부터 [오프닝, 답변]을 history로 백엔드에 보낸다(StrictMode 이중 실행 방지).
    useEffect(() => {
        if (startedRef.current) return
        startedRef.current = true
        setMessages([{ role: 'assistant', content: OPENING_MESSAGE }])
        setLastTypedIndex(0)
        setChoices(OPENING_CHOICES)
    }, [])

    const answer = (text: string) => {
        const trimmed = text.trim()
        if (!trimmed || busy) return
        const next: DefineMessage[] = [
            ...messages,
            { role: 'user', content: trimmed },
        ]
        setMessages(next)
        setInput('')
        void advance(next)
    }

    const handleSubmit = (e: FormEvent) => {
        e.preventDefault()
        answer(input)
    }

    const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key !== 'Enter' || e.shiftKey || e.nativeEvent.isComposing) return
        e.preventDefault()
        answer(input)
    }

    return (
        <Modal
            open
            onClose={onClose}
            title={
                <span className='flex items-center gap-2'>
                    <span className='bg-accent text-primary grid size-6 place-items-center rounded-full'>
                        <Sparkles className='size-3.5' />
                    </span>
                    AI로 정의
                </span>
            }
            className='overflow-hidden'>
            <div className='-m-5 flex max-h-[70vh] min-h-0 flex-col'>
                <div
                    ref={scrollRef}
                    className='min-h-0 flex-1 space-y-3 overflow-y-auto px-4 py-3'>
                    {messages.map((m, i) => {
                        const isUser = m.role === 'user'
                        return (
                            <div
                                key={i}
                                className={`flex w-full ${
                                    isUser ? 'justify-end' : 'justify-start'
                                }`}>
                                <div
                                    className={`max-w-[85%] min-w-0 rounded-md px-3.5 py-2.5 text-sm leading-relaxed ${
                                        isUser
                                            ? 'bg-primary text-primary-foreground whitespace-pre-wrap'
                                            : 'border-border bg-background text-foreground border'
                                    }`}>
                                    {!isUser && i === lastTypedIndex ? (
                                        <Typewriter
                                            text={m.content}
                                            markdown={false}
                                            onTick={scrollToBottom}
                                        />
                                    ) : isUser ? (
                                        m.content
                                    ) : (
                                        <span className='whitespace-pre-wrap'>
                                            {m.content}
                                        </span>
                                    )}
                                </div>
                            </div>
                        )
                    })}

                    {busy ? <ChatThinking /> : null}

                    {error ? (
                        <p className='text-destructive text-sm'>{error}</p>
                    ) : null}

                    {!busy && choices.length > 0 ? (
                        <div className='flex flex-wrap gap-2 pt-1'>
                            {choices.map((c) => (
                                <button
                                    key={c}
                                    type='button'
                                    onClick={() => answer(c)}
                                    className='border-primary/40 text-primary hover:bg-accent rounded-full border px-3 py-1.5 text-sm transition-colors'>
                                    {c}
                                </button>
                            ))}
                        </div>
                    ) : null}
                </div>

                <form
                    onSubmit={handleSubmit}
                    className='border-border flex items-end gap-2 border-t px-4 py-3'>
                    <textarea
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={handleKeyDown}
                        rows={1}
                        disabled={busy || done}
                        placeholder={
                            done
                                ? '정의가 끝났어요'
                                : '보기를 누르거나 직접 입력하세요'
                        }
                        className='border-border bg-background text-foreground placeholder:text-muted-foreground focus-visible:ring-ring max-h-40 min-h-[2.5rem] flex-1 resize-none rounded-md border px-3 py-2 text-sm focus-visible:ring-2 focus-visible:outline-none disabled:opacity-60'
                    />
                    <Button
                        type='submit'
                        disabled={busy || done || !input.trim()}>
                        전송
                    </Button>
                </form>
            </div>
        </Modal>
    )
}
